import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RegisteredHoursDialog } from '../../RegisteredHoursDialog';
import { registeredHoursLabel } from '../../registered-hours';
import React, { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Building, ClipboardExport, Clock, DocumentText, Eye, Import, Refresh, Timer1, User } from 'iconsax-react';
import { TableHeader } from '../../../../../components/common/header/tableHeader/TableHeader';
import { colCenter } from '../../../../../style/style';
import { palette } from '../../../../../../theme/palette';
import { AcceptHoursDialog } from '../../single-user-hours/dialog/AceptHoursDialog';
import { OpenTimesheetDialog } from '../../single-user-hours/dialog/OTimeShetDialog';
import { downloadCOCDocumentAsync } from '@application/features/freelancer/freelancerThunks';
import { useAppDispatch } from '@application/store/useStore';
import { convertBase64toBlob, getMimeTypeFromBase64, openOrDownloadPdf } from '@application/common/helpers/file-helper';
import { ContractEndDateExtendConfirmationDialog } from '../../single-user-hours/dialog/ContractEndDateExtendConfirmationDialog';
import { ClientContractStatus } from '@application/common/enums/client-contract-status';
import { SyncConfirmationDialog } from '../dialog/syncConfirmationDialog';
import { reSyncBusinessCentralHoursByTimeSheetIdsAsync } from '@application/features/HoursRegistration/hoursRegistrationThunk';
const theme = palette('light');
export const UserHourTable = ({ tab, onSelectionChange, timeSheetData, searchQuery, onActionComplete, fetchTimeSheetData }) => {
    const dispatch = useAppDispatch();
    const [detailRow, setDetailRow] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const islg = useMediaQuery('(max-width:800px)');
    const isXl = useMediaQuery('(max-width:1612px)');
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogForContractEndDateExtendConfirmation, setDialogForContractEndDateExtendConfirmation] = useState(false);
    const [openAcceptedHourDialog, setOpenAcceptedHourDialog] = useState(false);
    const [commaSeparatedIds, setSelectedRowIds] = useState(null);
    const [selectedDeclinedRow, setSelectedDeclinedRow] = useState();
    const [selectedClientName, setSelectedClientName] = useState();
    const [selectedMonth, setSelectedMonth] = useState();
    const [confirmSync, setConfirmSync] = useState(false);
    const [selectedTimeSheetIds, setSelectedTimeSheetIds] = useState('');
    const filteredData = useMemo(() => {
        if (searchQuery && searchQuery !== '') {
            return (timeSheetData || []).filter((timeSheet) => timeSheet?.freelancerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                timeSheet?.allClientNames?.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return timeSheetData || [];
    }, [searchQuery, timeSheetData]);
    const columns = [
        {
            field: 'freelancerName',
            headerName: 'Name',
            flex: islg ? undefined : 1,
            width: islg ? 180 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: User, text: 'Name' }),
            renderCell: ({ row }) => islg ? _jsxs(Box, { sx: { py: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: 'body2', sx: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: row.freelancerName }), _jsx(Typography, { variant: 'caption', display: 'block', sx: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: row.allClientNames }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: row.monthName })] }) : row.freelancerName
        },
        {
            field: 'freelancerPosition',
            headerName: 'Position',
            flex: islg ? undefined : 1,
            width: islg ? 180 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText, text: 'Position' })
        },
        {
            field: 'allClientNames',
            headerName: 'Client',
            flex: islg ? undefined : 1,
            width: islg ? 200 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: Building, text: 'Client' })
        },
        {
            field: 'monthName',
            headerName: 'Month',
            flex: isXl ? undefined : 0.9,
            width: isXl ? 150 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: Timer1, text: 'Month' })
        }
    ];
    columns.push({ field: 'totalHours', headerName: 'Registered hours', minWidth: 155, flex: 0.8,
        renderCell: ({ row }) => _jsx("span", { children: registeredHoursLabel(row.totalHours, row.hasHoursConflict) }) });
    const convertUrlFileToPDF = (fileName, folderName) => {
        const fileDto = { folderName: folderName, fileName: fileName };
        dispatch(downloadCOCDocumentAsync(fileDto))
            .unwrap()
            .then((res) => {
            const mimeType = getMimeTypeFromBase64(res.file);
            if (mimeType === 'application/pdf') {
                openOrDownloadPdf(res.file, fileName, 'Chamber of Commerce Document');
            }
            else {
                const blob = convertBase64toBlob(res.file, mimeType ?? '');
                if (!blob) {
                    console.error('Failed to create Blob from the base64 string.');
                    return;
                }
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName || 'download';
                link.click();
                URL.revokeObjectURL(url);
            }
        })
            .catch(() => { });
    };
    const handleConfirmSync = () => {
        dispatch(reSyncBusinessCentralHoursByTimeSheetIdsAsync(selectedTimeSheetIds))
            .unwrap()
            .then((resp) => {
            // dispatch(getTimeSheetForAdminUsingStatus(HoursStatus.Approved));
            fetchTimeSheetData();
        })
            .catch(() => { });
        setSelectedTimeSheetIds('');
        setConfirmSync(false);
    };
    if (tab === 1 || tab === 3) {
        columns.push({
            field: 'actions',
            headerName: 'Review',
            headerAlign: 'left',
            align: 'left',
            flex: islg ? undefined : 1,
            width: islg ? 150 : undefined,
            renderHeader: () => (_jsxs(Box, { sx: { ...colCenter, gap: 2 }, children: [_jsx(ClipboardExport, { style: { color: theme.primary.main } }), _jsx(Typography, { variant: 'subtitle2', color: 'white', children: "Actions" })] })),
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [tab === 1 && _jsx(IconButton, { "aria-label": 'Review submitted hours', disabled: !!params.row.hasHoursConflict, onClick: () => {
                            setSelectedRowIds(params.id);
                            setSelectedClientName(params.row.allClientNames);
                            setSelectedMonth(params.row.monthName);
                            if (tab === 1) {
                                handleOpenDialog();
                            }
                            else {
                                setDetailRow(params.row);
                            }
                        }, children: _jsx(Eye, {}) }), tab === 3 && params.row.assignmentStatus == ClientContractStatus.Expired && (_jsx(Tooltip, { title: 'Extend deadline', children: _jsx(IconButton, { onClick: () => {
                                setSelectedDeclinedRow(params.row);
                                setDialogForContractEndDateExtendConfirmation(true);
                            }, children: _jsx(Clock, {}) }) }))] }))
        });
    }
    else if (tab === 2) {
        columns.push({
            field: 'action',
            headerName: 'Action',
            headerAlign: 'left',
            align: 'left',
            flex: islg ? undefined : 0.8,
            width: islg ? 100 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: ClipboardExport, text: 'Actions' }),
            renderCell: (params) => (_jsx(IconButton, { "aria-label": 'Download timesheet', disabled: !params.row.fileName || !params.row.folderName, onClick: () => {
                    if (params.row.fileName && params.row.folderName)
                        convertUrlFileToPDF(params.row.fileName, params.row.folderName);
                }, children: _jsx(Import, { "aria-label": 'Download timesheet' }) }))
        });
        //Resync Column
        columns.push({
            field: 'resync',
            headerName: 'Resync',
            headerAlign: 'left',
            align: 'left',
            flex: islg ? undefined : 0.8,
            width: islg ? 100 : undefined,
            renderHeader: () => _jsx(TableHeader, { icon: Refresh, text: 'Sync' }),
            renderCell: (params) => {
                if (!params.row.isTimesheetDataSyncInBC) {
                    return null;
                }
                return (_jsx(IconButton, { onClick: () => {
                        setSelectedTimeSheetIds(params.row.timeSheetIds);
                        setConfirmSync(true);
                    }, children: _jsx(Refresh, {}) }));
            }
        });
    }
    columns.push({
        field: 'registeredHoursDetail', headerName: 'Details', width: 90, sortable: false,
        renderCell: ({ row }) => _jsx(Tooltip, { title: 'View registered hours', children: _jsx("span", { children: _jsx(IconButton, { "aria-label": `View registered hours for ${row.freelancerName}, ${row.monthName}`, disabled: !!row.hasHoursConflict, onClick: () => setDetailRow(row), children: _jsx(Eye, {}) }) }) })
    });
    const getRowClassName = (params) => {
        const rowId = params.id;
        if (selectedRows.includes(rowId)) {
            return 'selected-row';
        }
        return '';
    };
    const handleOpenDialog = () => {
        setOpenDialog(true);
    };
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };
    const closePopupForContractEndDateConfirmation = () => {
        setDialogForContractEndDateExtendConfirmation(false);
    };
    const handleOpenReadonlyDialog = () => {
        setOpenAcceptedHourDialog(true);
    };
    const handleCloseReadonlyDialog = () => {
        setOpenAcceptedHourDialog(false);
    };
    return (_jsxs("div", { style: {
            width: '100%',
            height: 'calc(100svh - 300px)',
            overflow: 'auto',
            background: 'white',
            borderRadius: '25px'
        }, children: [_jsx(DataGrid, { sx: {
                    borderRadius: '25px',
                    height: 'calc(100svh - 300px)',
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: theme.black.main,
                        color: '#ffffff',
                        borderRadius: '25px',
                        marginBottom: '15px'
                    },
                    '& .MuiDataGrid-cell': {
                        border: 'none',
                        paddingLeft: '16px',
                        paddingRight: '16px'
                    },
                    '& .MuiDataGrid-row': {
                        background: 'white !important'
                    },
                    '& .MuiDataGrid-row.selected-row': {
                        background: '#FFF7E0 !important'
                    },
                    '& .MuiDataGrid-main': {
                        background: theme.background.neutral
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        background: 'white'
                    },
                    '& .MuiDataGrid-cell:focus': {
                        outline: 'none'
                    },
                    '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none'
                    }
                }, rows: filteredData.map((data) => ({
                    ...data,
                    id: data.timeSheetIds
                })), columnVisibilityModel: { freelancerPosition: !islg, allClientNames: !islg, monthName: !islg }, rowHeight: islg ? 88 : 52, columns: [
                    ...['freelancerName', 'totalHours', 'registeredHoursDetail'].map(field => columns.find(column => column.field === field)),
                    ...columns.filter(column => !['freelancerName', 'totalHours', 'registeredHoursDetail'].includes(column.field))
                ].map((column) => ({
                    ...column,
                    ...(islg && column.field === 'freelancerName' ? { width: 160 } : {}),
                    ...(islg && column.field === 'totalHours' ? { headerName: 'Hours', renderHeader: () => _jsx("span", { children: "Hours" }), width: 100, minWidth: 100, flex: undefined } : {}),
                    ...(islg && column.field === 'registeredHoursDetail' ? { width: 70 } : {}),
                    sortable: false
                })), hideFooterPagination: true, hideFooterSelectedRowCount: true, hideFooter: true, disableColumnMenu: true, getRowClassName: getRowClassName }), _jsx(RegisteredHoursDialog, { row: detailRow, onClose: () => setDetailRow(null) }), _jsx(OpenTimesheetDialog, { open: openDialog, handleClose: handleCloseDialog, timeSheetIds: commaSeparatedIds, onActionComplete: onActionComplete, clientName: selectedClientName, monthAndYear: selectedMonth }), _jsx(AcceptHoursDialog, { open: openAcceptedHourDialog, handleClose: handleCloseReadonlyDialog, timeSheetIds: commaSeparatedIds, clientName: selectedClientName, monthAndYear: selectedMonth }), _jsx(ContractEndDateExtendConfirmationDialog, { open: dialogForContractEndDateExtendConfirmation, handleClose: closePopupForContractEndDateConfirmation, selectedRow: selectedDeclinedRow }), _jsx(SyncConfirmationDialog, { open: confirmSync, handleClose: () => {
                    setConfirmSync(false);
                    setSelectedTimeSheetIds('');
                }, handleSubmit: handleConfirmSync })] }));
};
export default UserHourTable;
